import { Autocomplete } from "@mui/material";
import { Axios } from "api/axios";
import BootstrapTextField from "Common/BootstrapTextField";
import SubmitButton from "Components/Buttons/SubmitButton";
import { autocompleteDropdownStyles } from "Constants/Style";
import { City, Country, State } from "country-state-city";
import React, { useEffect, useState } from "react";
import { Button, Col, Form, InputGroup, Row } from "react-bootstrap";
import { BsEnvelope, BsTelephone } from "react-icons/bs";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

interface AddCustomerFormQuatationProps {
  setShowCustomerModal: (value: boolean) => void;
  onCustomerAdded?: (customer: any) => void;
}

const AddCustomerFormQuatation: React.FC<AddCustomerFormQuatationProps> = ({
  setShowCustomerModal,
  onCustomerAdded,
}: any) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { customerData } = location.state || {};
  const { id } = useParams<{ id: string }>();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [existingPANs, setExistingPANs] = useState<string[]>([]);
  const [existingGSTINs, setExistingGSTINs] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    customer_name: "",
    mobile_no: "",
    email: "",
    contact_person_name: "",
    contact_person_mobile_no: "",
    pan_number: "",
    gstin: "",
    address: "",
    area: "",
    country: "",
    state: "",
    city: "",
    pincode: "",
  });

  // Load countries on mount
  useEffect(() => {
    const countryList = Country.getAllCountries();
    setCountries(countryList);
    
    // Auto-select India for new customer creation (when id is empty and country is empty)
    if (!id && !formData.country) {
      const india = countryList.find((country: any) => country.name === "India");
      if (india) {
        setFormData((prev) => {
          if (!prev.country) {
            // Load states for India
            const indiaStates = State.getStatesOfCountry(india.isoCode);
            setStates(indiaStates);
            
            return {
              ...prev,
              country: india.name,
            };
          }
          return prev;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

 
  //   loadCustomer();
  // }, [id, customerData]);

  const handleCountryChange = (e: any, selectedCountry: any) => {
    setFormData((prev) => ({
      ...prev,
      country: selectedCountry?.name || "",
      state: "",
      city: "",
    }));
    if (selectedCountry) {
      setIsLoadingStates(true);
      const statesList = State.getStatesOfCountry(selectedCountry.isoCode);
      setStates(statesList);
      setCities([]);
      setIsLoadingStates(false);
    }
  };

  const handleStateChange = (e: any, selectedState: any) => {
    const selectedCountry = countries.find((c) => c.name === formData.country);
    setFormData((prev) => ({
      ...prev,
      state: selectedState?.name || "",
      city: "",
    }));
    if (selectedCountry && selectedState) {
      setIsLoadingCities(true);
      const citiesList = City.getCitiesOfState(
        selectedCountry.isoCode,
        selectedState.isoCode
      );
      setCities(citiesList);
      setIsLoadingCities(false);
    }
  };

  const handleCityChange = (e: any, selectedCity: any) => {
    setFormData((prev) => ({ ...prev, city: selectedCity?.name || "" }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Remove error immediately if field is valid
    setErrors((prev) => {
      const updated = { ...prev };
      if (name === "customer_name") {
        if (value && /^[A-Za-z\s]+$/.test(value)) delete updated.customer_name;
      }
      if (name === "mobile_no") {
        if (value) delete updated.mobile_no;
      }
      if (name === "email") {
        if (value) delete updated.email;
      }
      return updated;
    });
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.customer_name)
      newErrors.customer_name = "Customer Name is required";
    else if (!/^[A-Za-z\s]+$/.test(formData.customer_name))
      newErrors.customer_name = "Only alphabets and spaces are allowed";

    if (!formData.mobile_no) newErrors.mobile_no = "Mobile Number is required";
    if (!formData.email) newErrors.email = "Email is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setIsSubmitting(true);
      let response = await Axios.post("api/customers/create/", formData);

      const newCustomer = response.data.data;
      toast.success(`Customer ${id ? "updated" : "added"} succes  ly`);
      if (onCustomerAdded) onCustomerAdded(newCustomer);
      setShowCustomerModal(false);
    } catch (error: any) {
      const backendErrors = error?.response?.data?.errors;
      const backendMessage = error?.response?.data?.message;

      const newErrors: Record<string, string> = {};
      if (
        backendErrors &&
        typeof backendErrors === "object" &&
        !Array.isArray(backendErrors)
      ) {
        Object.keys(backendErrors).forEach((key) => {
          newErrors[key] = backendErrors[key].join(", ");
        });
      } else if (typeof backendErrors === "string")
        newErrors["email"] = backendErrors;

      // setErrors((prev) => ({ ...prev, ...newErrors }));
      toast.error(
        Object.values(newErrors)[0] ||
          backendMessage ||
          "Failed to save customer"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-form-container">
      {/* <ToastContainer /> */}
      <div className="p-4 bg-white rounded shadow-sm">
        {/* <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
          <div className="d-flex align-items-center">
            <span
              className="me-2"
              style={{ fontSize: "1.25rem", cursor: "pointer" }}
              onClick={() => navigate(-1)}
            >
              <BsArrowLeft />
            </span>
            <h5 className="mb-0 fw-bold">{id ? "Edit" : "Add"} Customer</h5>
          </div>
          <div className="text-muted" style={{ fontSize: "0.9rem" }}>
            <Link to="/customer" className="text-decoration-none text-muted">
              Customer
            </Link>{" "}
            &gt;
            <span className="text-muted">
              {id ? "Edit Customer" : "Add Customer"}
            </span>
          </div>
        </div> */}

        <Form autoComplete="off" onSubmit={handleSubmit}>
          {/* Customer Details */}
          <div className="mb-4">
            <h6 className="fw-bold border-bottom pb-2">Customer Details</h6>
            <Row className="g-3 mt-2">
              {/* Customer Name */}
              <Col md={6}>
                <Form.Label>
                  Customer Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="customer_name"
                  placeholder="Enter Customer Name"
                  value={formData.customer_name}
                  onChange={(e: any) =>
                    /^[A-Za-z\s]*$/.test(e.target.value) && handleChange(e)
                  }
                  isInvalid={!!errors.customer_name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.customer_name}
                </Form.Control.Feedback>
              </Col>

              {/* Customer Mobile no. */}
              <Col md={6}>
                <Form.Label>
                  Mobile Number <span className="text-danger">*</span>
                </Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <BsTelephone /> +91
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="mobile_no"
                    placeholder="Enter Mobile Number"
                    value={formData.mobile_no}
                    onChange={handleChange}
                    isInvalid={!!errors.mobile_no}
                    maxLength={10}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.mobile_no}
                  </Form.Control.Feedback>
                </InputGroup>
              </Col>

              {/* Customer Email */}
              <Col md={6}>
                <Form.Label>
                  Email <span className="text-danger">*</span>
                </Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <BsEnvelope />
                  </InputGroup.Text>
                  <Form.Control
                    type="email"
                    name="email"
                    placeholder="Enter Email"
                    value={formData.email}
                    onChange={handleChange}
                    isInvalid={!!errors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                </InputGroup>
              </Col>

              <Col md={6}>
                <Form.Label>Contact Person Name</Form.Label>
                <Form.Control
                  type="text"
                  name="contact_person_name"
                  placeholder="Enter Contact Person Name"
                  value={formData.contact_person_name}
                  onChange={(e: any) =>
                    /^[A-Za-z\s]*$/.test(e.target.value) && handleChange(e)
                  }
                  isInvalid={!!errors.contact_person_name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.contact_person_name}
                </Form.Control.Feedback>
              </Col>

              <Col md={6}>
                <Form.Label>Contact Person Mobile Number </Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <BsTelephone /> +91
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="contact_person_mobile_no"
                    placeholder="Enter Contact Person Mobile Number"
                    value={formData.contact_person_mobile_no}
                    onChange={handleChange}
                    isInvalid={!!errors.contact_person_mobile_no}
                    maxLength={10}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.contact_person_mobile_no}
                  </Form.Control.Feedback>
                </InputGroup>
              </Col>

              <Col md={6}>
                <Form.Label>PAN Number</Form.Label>
                <Form.Control
                  type="text"
                  name="pan_number"
                  placeholder="Enter PAN Number"
                  value={formData.pan_number}
                  onChange={handleChange}
                  isInvalid={!!errors.pan_number}
                  maxLength={10}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.pan_number}
                </Form.Control.Feedback>
              </Col>

              <Col md={6}>
                <Form.Label>GSTIN</Form.Label>
                <Form.Control
                  type="text"
                  name="gstin"
                  placeholder="Enter GSTIN"
                  value={formData.gstin}
                  onChange={handleChange}
                  isInvalid={!!errors.gstin}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.gstin}
                </Form.Control.Feedback>
              </Col>
            </Row>
          </div>
          {/* Address Details */}
          <div className="mb-4">
            <h6 className="fw-bold border-bottom pb-2">Address Details</h6>
            <Row className="g-3 mt-2">
              <Col lg={6}>
                <Form.Label>Address</Form.Label>
                <Form.Control
                  type="text"
                  name="address"
                  placeholder="Enter Address"
                  value={formData.address}
                  onChange={handleChange}
                  isInvalid={!!errors.address}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.address}
                </Form.Control.Feedback>
              </Col>
              <Col lg={6}>
                <Form.Label>Area</Form.Label>
                <Form.Control
                  type="text"
                  name="area"
                  placeholder="Enter Area"
                  value={formData.area}
                  onChange={handleChange}
                  isInvalid={!!errors.area}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.area}
                </Form.Control.Feedback>
              </Col>
              <Col md={4}>
                <Form.Label>Country</Form.Label>
                <Autocomplete
                  disablePortal
                  options={countries}
                  getOptionLabel={(option) => option.name}
                  value={
                    countries.find((c) => c.name === formData.country) || null
                  }
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (option) => option.name
                    )
                  }
                  onChange={handleCountryChange}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select Country"
                      size="small"
                      error={!!errors.country}
                      helperText={errors.country}
                      fullWidth
                      sx={autocompleteDropdownStyles.inputSx}
                    />
                  )}
                />
              </Col>
              <Col md={4}>
                <Form.Label>State</Form.Label>
                <Autocomplete
                  disablePortal
                  options={states}
                  getOptionLabel={(option) => option.name}
                  loading={isLoadingStates}
                  value={states.find((s) => s.name === formData.state) || null}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (option) => option.name
                    )
                  }
                  onChange={handleStateChange}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select State"
                      size="small"
                      error={!!errors.state}
                      helperText={errors.state}
                      fullWidth
                      sx={autocompleteDropdownStyles.inputSx}
                    />
                  )}
                  disabled={!formData.country}
                />
              </Col>
              <Col md={4}>
                <Form.Label>City</Form.Label>
                <Autocomplete
                  disablePortal
                  options={cities}
                  getOptionLabel={(option) => option.name}
                  loading={isLoadingCities}
                  value={cities.find((c) => c.name === formData.city) || null}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (option) => option.name
                    )
                  }
                  onChange={handleCityChange}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select City"
                      size="small"
                      error={!!errors.city}
                      helperText={errors.city}
                      fullWidth
                      sx={autocompleteDropdownStyles.inputSx}
                    />
                  )}
                  disabled={!formData.state}
                />
              </Col>
              <Col md={4}>
                <Form.Label>Pincode</Form.Label>
                <Form.Control
                  type="text"
                  name="pincode"
                  placeholder="Enter Pincode"
                  value={formData.pincode}
                  onChange={handleChange}
                  isInvalid={!!errors.pincode}
                  maxLength={6}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.pincode}
                </Form.Control.Feedback>
              </Col>
            </Row>
          </div>

          {/* Actions */}
          <div className="d-flex justify-content-center gap-3 py-3">
            <Button
              variant="outline-secondary"
              onClick={() => setShowCustomerModal(false)}
              style={{
                color: "#878899",
                borderColor: "#B0BAC9",
                backgroundColor: "transparent",
              }}
            >
              Cancel
            </Button>
            <SubmitButton isSubmitting={isSubmitting} isEdit={!!id} />
          </div>
        </Form>
      </div>
    </div>
  );
};

export default AddCustomerFormQuatation;
